import React, { PropsWithChildren, useMemo } from 'react';

import { Money } from '@betterment/core';
import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { CenteredLayout, H1 } from '@betterment/samba-react';
import { FullPageErrorFallback } from '@betterment/shared-components/src/components/error-fallbacks';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { FormikHelpers } from 'formik';

import { PendingOrderErrorPage } from './error-pages/PendingOrderErrorPage';
import { ValidateFinancialSecurityOrderRequestDocument } from './graphql/mutation.validateFinancialSecurityOrderRequest.generated';
import { SecurityPositionForSellOrderDocument } from './graphql/query.securityPosition.generated';
import { Header } from './Header';
import { InfoBanner } from './InfoBanner';
import { SellOrderFormValues, ValidationSchema } from './schema';
import { SellOrderFormLoaded } from './SellOrderFormLoaded';
import { SellOrderFormLoading } from './SellOrderFormLoading';
import {
  CoordinatedAccount,
  OrderUnit,
  SellOrderStepProps,
  toSubmittableOrderValue,
  toValidatedSellOrder,
} from './types';
import { formatAccountNameWithPurpose, is401kTaxationType } from './utils';

import { DisplayableError, FinancialSecurityOrderRequestDirection } from '../../../graphql/schema';
import { getAccountHolderNames } from '../../../Shared/accountHolders';
import { WorkflowPage } from '../../../Shared/Components/WorkflowPage';

function SellOrderFormContainer({ onCancel, children }: PropsWithChildren<{ onCancel: () => void }>) {
  return (
    <WorkflowPage>
      <Header onCancel={onCancel}>Sell order</Header>
      <WorkflowPage.Main css={{ pt: 'space.400', pb: 'space.800' }}>
        <CenteredLayout size="medium" css={{ rowGap: 'space.300' }}>
          <H1 variant="title400">Sell order</H1>
          <InfoBanner />
          {children}
        </CenteredLayout>
      </WorkflowPage.Main>
    </WorkflowPage>
  );
}

export const NewSellOrder: React.FC<SellOrderStepProps> = ({ goToStep, navigate, flowState }) => {
  const accountId = useRequiredParam('accountId');
  const financialSecurityId = useRequiredParam('financialSecurityId');

  const { data, error: securityPositionError } = useGraphQLQuery(SecurityPositionForSellOrderDocument, {
    variables: { managedAccountId: accountId, financialSecurityId },
    fetchPolicy: 'network-only',
  });

  const [validateOrder, { error: validateOrderError }] = useGraphQLMutation(
    ValidateFinancialSecurityOrderRequestDocument
  );

  const coordinatedAccounts: CoordinatedAccount[] = useMemo(
    () =>
      data?.securityPosition?.securityPositionsBySubAccount.map(position => ({
        id: position.subAccount.id,
        name: position.subAccount.name,
        amount: position.amount,
        shares: position.shares,
        is401kAccount: is401kTaxationType(position.subAccount.legalAccount.taxationType),
      })) ?? [],
    [data]
  );

  const onCancel = () => {
    void navigate(flowState.returnToPath);
  };

  if (securityPositionError) {
    return <FullPageErrorFallback />;
  }

  if (!data) {
    return (
      <SellOrderFormContainer onCancel={onCancel}>
        <SellOrderFormLoading />
      </SellOrderFormContainer>
    );
  }
  const { managedAccount, securityPosition } = data;

  if (!managedAccount || !securityPosition) {
    throw new Error('Managed account or security position not found.');
  }

  const assetTicker = securityPosition.symbol ?? '';
  const assetName = securityPosition.displayName ?? '';
  const hasPendingOrders = securityPosition.pendingOrders.length > 0;

  if (hasPendingOrders) {
    return (
      <PendingOrderErrorPage returnToPath={flowState.returnToPath} assetName={assetName} assetTicker={assetTicker} />
    );
  }

  const accountDisplayName = formatAccountNameWithPurpose(managedAccount.name, managedAccount.envelope.purpose?.name);
  const clientNames = getAccountHolderNames(managedAccount);
  const { isTaxCoordinated } = managedAccount;

  const selectableAccounts = coordinatedAccounts.filter(account => !account.is401kAccount);

  const pricePerShare =
    securityPosition?.amount && securityPosition.shares
      ? Money(Math.floor(securityPosition.amount.cents / securityPosition.shares))
      : Money(0);

  const handleFormSubmit = async (values: SellOrderFormValues, formikHelpers: FormikHelpers<SellOrderFormValues>) => {
    const selectedCoordinatedAccount = coordinatedAccounts.find(acc => acc.id === values.coordinatedAccountId)!;

    const validatedValues = toValidatedSellOrder({
      ...ValidationSchema.cast(values),
      assetName,
      assetTicker,
      pricePerShare,
      financialSecurityId,
      coordinatedAccount: selectedCoordinatedAccount,
    });

    const validationResult = await validateOrder({
      variables: {
        input: {
          direction: FinancialSecurityOrderRequestDirection.Sell,
          financialSecurityId,
          subAccountId: selectedCoordinatedAccount.id,
          value: toSubmittableOrderValue(validatedValues),
        },
      },
    });

    const validationResponse = validationResult.data?.validateFinancialSecurityOrderRequest;
    const displayableValidationErrors = validationResponse?.displayableErrors ?? [];
    const isAmountOrSharesError = (error: DisplayableError) =>
      error.path.includes('shares') || error.path.includes('amount');

    const amountOrSharesValidationErrors = displayableValidationErrors.filter(isAmountOrSharesError);
    const otherValidationErrors = displayableValidationErrors.filter(err => !isAmountOrSharesError(err));

    if (amountOrSharesValidationErrors.length > 0) {
      const amountFieldErrorMessage = amountOrSharesValidationErrors.map(e => e.message).join(', ');
      formikHelpers.setFieldTouched('amount');
      formikHelpers.setFieldError('amount', amountFieldErrorMessage);
      return;
    }

    if (validateOrderError || otherValidationErrors.length > 0 || !validationResponse?.result) {
      formikHelpers.setFieldTouched('_base');
      formikHelpers.setFieldError('_base', 'Something went wrong. Please review your order.');
      return;
    }

    const isUserSelectedSellAll = values.sellAll === true;

    const isDollarBasedSellingMoreThan90Percent = validationResponse.result.aboveSellAllThreshold;

    const shouldSellAll = isUserSelectedSellAll || isDollarBasedSellingMoreThan90Percent;

    const shouldShowWarning = isDollarBasedSellingMoreThan90Percent && !isUserSelectedSellAll;

    const nextStep = shouldShowWarning ? 'sell_all_warning' : 'review';

    goToStep(nextStep, {
      amount: validatedValues.amount,
      unit: validatedValues.unit,
      assetName,
      assetTicker,
      pricePerShare,
      financialSecurityId,
      coordinatedAccount: selectedCoordinatedAccount,
      sellAll: shouldSellAll,
    });
  };

  const getInitialFormValues = (): SellOrderFormValues => {
    if (!flowState.values || !flowState.values.coordinatedAccount) {
      const defaultCoordinatedAccount = selectableAccounts.length === 1 ? selectableAccounts[0] : null;
      return {
        amount: '',
        heldAmount: defaultCoordinatedAccount?.amount ?? Money(0),
        unit: OrderUnit.Dollars,
        assetTicker,
        coordinatedAccountId: defaultCoordinatedAccount?.id ?? '',
        sellAll: false,
      };
    }

    const { unit } = flowState.values;
    const heldAmount =
      unit === OrderUnit.Dollars
        ? flowState.values.coordinatedAccount.amount
        : flowState.values.coordinatedAccount.shares;

    return {
      ...flowState.values,
      amount: flowState.values.amount ?? '',
      heldAmount,
      coordinatedAccountId: flowState.values.coordinatedAccount.id,
    };
  };

  return (
    <SellOrderFormContainer onCancel={onCancel}>
      <SellOrderFormLoaded
        accountDisplayName={accountDisplayName}
        assetName={assetName}
        assetTicker={assetTicker}
        coordinatedAccounts={selectableAccounts}
        handleSubmit={handleFormSubmit}
        initialFormValues={getInitialFormValues()}
        isTaxCoordinated={isTaxCoordinated}
        pricePerShare={pricePerShare}
        clientNames={clientNames}
      />
    </SellOrderFormContainer>
  );
};
